import { useEffect, useMemo, useRef, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
  ZoomableGroup,
} from "react-simple-maps";
import { Activity, Radio, Zap, ShieldAlert, Globe, Filter, Pause, Play, Rewind, FastForward, History, Wifi, Maximize2, Minimize2, Volume2, VolumeX, Map as MapIcon, Orbit } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api, API_BASE } from "@/lib/api";
import ThreatGlobe from "@/pages/ThreatGlobe";
import GlobeErrorBoundary from "@/pages/GlobeErrorBoundary";
import CVETicker from "@/pages/kiosk/CVETicker";
import AttackStoryPanel from "@/pages/AttackStoryPanel";
import { hasWebGLSupport } from "@/lib/webgl";
import { toast } from "sonner";

// Public world atlas topojson (110m — small, fast, dark-friendly)
const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const SEVERITY_STYLES = {
  low:      { color: "#00F0FF", ring: "#00F0FF", glow: "0 0 12px #00F0FF88", label: "LOW" },
  medium:   { color: "#FFC542", ring: "#FFC542", glow: "0 0 12px #FFC54288", label: "MED" },
  high:     { color: "#FF6B35", ring: "#FF6B35", glow: "0 0 16px #FF6B35AA", label: "HIGH" },
  critical: { color: "#FF2E63", ring: "#FF2E63", glow: "0 0 22px #FF2E63CC", label: "CRIT" },
};

const MAX_EVENTS = 60;      // rolling buffer size
const MAX_ARCS = 22;        // simultaneous animated arcs
const MAX_TICKER = 40;

function useEventStream({ enabled, simulate, real }) {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const esRef = useRef(null);

  // Seed with a snapshot on mount only (playback replaces events wholesale).
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get("/threat-map/snapshot?count=30");
        if (ignore) return;
        setEvents(r.data.events.slice(-MAX_EVENTS));
      } catch (e) {
        // silent — stream will still populate
      }
    })();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!enabled) {
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
      return;
    }
    const token = localStorage.getItem("forensiq_token");
    const url = `${API_BASE}/threat-map/stream?simulate=${simulate}&real=${real}`;
    const controller = new AbortController();

    (async () => {
      try {
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!resp.ok || !resp.body) {
          setConnected(false);
          return;
        }
        setConnected(true);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const chunks = buf.split("\n\n");
          buf = chunks.pop() ?? "";
          for (const chunk of chunks) {
            const lines = chunk.split("\n");
            const isEvent = lines.some((l) => l.startsWith("event:"));
            const dataLine = lines.find((l) => l.startsWith("data:"));
            if (!dataLine) continue;
            const payload = dataLine.slice(5).trim();
            if (!payload || isEvent) continue;
            try {
              const ev = JSON.parse(payload);
              if (!ev.source || !ev.target) continue;
              setEvents((prev) => {
                const next = [...prev, ev];
                return next.length > MAX_EVENTS
                  ? next.slice(next.length - MAX_EVENTS)
                  : next;
              });
            } catch {
              // ignore malformed
            }
          }
        }
      } catch (e) {
        // aborted or disconnected
      } finally {
        setConnected(false);
      }
    })();

    return () => controller.abort();
  }, [enabled, simulate, real]);

  return { events, setEvents, connected };
}


/* ---------- Playback engine ---------- */
const PLAYBACK_WINDOWS = [
  { label: "15m", minutes: 15 },
  { label: "1h",  minutes: 60 },
  { label: "6h",  minutes: 360 },
  { label: "24h", minutes: 1440 },
];
const PLAYBACK_SPEEDS = [1, 5, 20, 60, 120];

function usePlaybackEngine({ enabled, windowMinutes, speed, playing, setEvents, onProgress, onDone }) {
  // Fetched buffer of past events (chronological), and internal cursor.
  const [buffer, setBuffer] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);
  const cursorRef = useRef(0);            // index of next event to emit
  const virtualTimeRef = useRef(0);       // ms since window start
  const rafRef = useRef(null);
  const lastFrameRef = useRef(null);
  const seekToRef = useRef(null);

  // Load events whenever mode is enabled or window changes.
  useEffect(() => {
    if (!enabled) return;
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/threat-map/history?minutes=${windowMinutes}&limit=2000`);
        if (ignore) return;
        // Attach parsed ts millis for scheduling.
        const evs = (r.data.events || []).map((e) => ({
          ...e,
          _tms: new Date(e.ts).getTime(),
        })).sort((a, b) => a._tms - b._tms);
        setBuffer(evs);
        setBuckets(r.data.buckets || []);
        cursorRef.current = 0;
        virtualTimeRef.current = 0;
        setEvents([]);   // reset map
        onProgress?.(0);
      } catch (e) {
        setBuffer([]);
        setBuckets([]);
      } finally {
        setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [enabled, windowMinutes, setEvents, onProgress]);

  // Frame loop: advances virtual time and emits due events.
  useEffect(() => {
    if (!enabled || !playing || buffer.length === 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastFrameRef.current = null;
      return;
    }

    const startTs = buffer[0]._tms;
    const endTs = buffer[buffer.length - 1]._tms;
    const totalMs = Math.max(1, endTs - startTs);

    const step = (now) => {
      if (seekToRef.current !== null) {
        virtualTimeRef.current = seekToRef.current;
        // reset cursor to the correct position
        const targetTs = startTs + seekToRef.current;
        let idx = 0;
        while (idx < buffer.length && buffer[idx]._tms <= targetTs) idx += 1;
        cursorRef.current = idx;
        // rebuild the tail of already-past events onto the map
        const tail = buffer.slice(Math.max(0, idx - MAX_EVENTS), idx);
        setEvents(tail);
        seekToRef.current = null;
      }

      if (lastFrameRef.current == null) lastFrameRef.current = now;
      const dt = now - lastFrameRef.current;
      lastFrameRef.current = now;
      virtualTimeRef.current += dt * speed;

      const targetTs = startTs + virtualTimeRef.current;
      while (
        cursorRef.current < buffer.length &&
        buffer[cursorRef.current]._tms <= targetTs
      ) {
        const ev = buffer[cursorRef.current];
        setEvents((prev) => {
          const next = [...prev, ev];
          return next.length > MAX_EVENTS ? next.slice(-MAX_EVENTS) : next;
        });
        cursorRef.current += 1;
      }

      onProgress?.(Math.min(1, virtualTimeRef.current / totalMs));

      if (cursorRef.current >= buffer.length) {
        onDone?.();
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastFrameRef.current = null;
    };
  }, [enabled, playing, buffer, speed, setEvents, onProgress, onDone]);

  const seek = (fraction) => {
    if (!buffer.length) return;
    const startTs = buffer[0]._tms;
    const endTs = buffer[buffer.length - 1]._tms;
    seekToRef.current = Math.max(0, Math.min(1, fraction)) * (endTs - startTs);
  };

  const restart = () => {
    seekToRef.current = 0;
  };

  return { buffer, buckets, loading, seek, restart };
}

function FilterPill({ label, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={`threatmap-filter-${label.toLowerCase()}`}
      className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors ${
        active
          ? "text-black"
          : "text-[#8E9196] hover:text-white border-white/10"
      }`}
      style={active ? { background: color, borderColor: color } : undefined}
    >
      {label}
    </button>
  );
}

function PlaybackControls({
  windowMinutes, setWindowMinutes,
  speed, setSpeed,
  playing, setPlaying,
  progress, onSeek, onRestart,
  buffer, buckets, loading,
}) {
  const trackRef = useRef(null);

  const handleTrackClick = (e) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const frac = (e.clientX - rect.left) / rect.width;
    onSeek(frac);
  };

  const totalEvents = buffer.length;
  const cursorIdx = Math.floor(progress * totalEvents);
  const currentEvent = buffer[Math.min(cursorIdx, totalEvents - 1)];
  const currentTs = currentEvent ? new Date(currentEvent.ts) : null;

  // Bucket histogram: normalize counts.
  const maxBucketCount = Math.max(1, ...buckets.map((b) => b.count));

  return (
    <div className="mt-4 flex items-center gap-4" data-testid="threatmap-playback-controls">
      {/* Window selector */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mr-1">Window</span>
        {PLAYBACK_WINDOWS.map((w) => (
          <button
            key={w.minutes}
            onClick={() => setWindowMinutes(w.minutes)}
            data-testid={`threatmap-pb-window-${w.label}`}
            className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors ${
              windowMinutes === w.minutes
                ? "bg-[#B57BFF] text-black border-[#B57BFF]"
                : "text-[#8E9196] hover:text-white border-white/10"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* Play / Restart */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPlaying((p) => !p)}
          data-testid="threatmap-pb-play"
          disabled={loading || totalEvents === 0}
          className="p-1.5 border border-white/10 hover:border-[#B57BFF]/60 rounded-sm text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={onRestart}
          data-testid="threatmap-pb-restart"
          disabled={loading || totalEvents === 0}
          className="p-1.5 border border-white/10 hover:border-[#B57BFF]/60 rounded-sm text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Restart"
        >
          <Rewind className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Speed */}
      <div className="flex items-center gap-1">
        <FastForward className="w-3 h-3 text-[#8E9196]" />
        {PLAYBACK_SPEEDS.map((s) => (
          <button
            key={s}
            onClick={() => setSpeed(s)}
            data-testid={`threatmap-pb-speed-${s}x`}
            className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors ${
              speed === s
                ? "bg-white text-black border-white"
                : "text-[#8E9196] hover:text-white border-white/10"
            }`}
          >
            {s}×
          </button>
        ))}
      </div>

      {/* Scrubber */}
      <div className="flex-1 min-w-[200px]">
        <div
          ref={trackRef}
          onClick={handleTrackClick}
          data-testid="threatmap-pb-scrubber"
          className="relative h-6 bg-[#0a0a0a] border border-white/10 rounded-sm cursor-pointer overflow-hidden"
        >
          {/* Bucket histogram (past 60 min shown left→right) */}
          <div className="absolute inset-0 flex items-end px-0.5">
            {buckets.slice().reverse().map((b, i) => {
              const color =
                b.max_sev >= 4 ? "#FF2E63" :
                b.max_sev >= 3 ? "#FF6B35" :
                b.max_sev >= 2 ? "#FFC542" : "#00F0FF";
              const h = Math.max(2, (b.count / maxBucketCount) * 22);
              return (
                <div
                  key={i}
                  className="flex-1 mx-[0.5px] opacity-40"
                  style={{ height: `${h}px`, background: color }}
                />
              );
            })}
          </div>
          {/* Progress fill */}
          <div
            className="absolute inset-y-0 left-0 bg-[#B57BFF]/25"
            style={{ width: `${progress * 100}%` }}
          />
          {/* Playhead */}
          <div
            className="absolute inset-y-0 w-0.5 bg-[#B57BFF]"
            style={{ left: `${progress * 100}%`, boxShadow: "0 0 6px #B57BFF" }}
          />
        </div>
        <div className="mt-1 flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-[#52525B]">
          <span>-{windowMinutes >= 60 ? `${windowMinutes / 60}h` : `${windowMinutes}m`}</span>
          <span className="text-[#B57BFF]">
            {loading ? "loading history…" :
             totalEvents === 0 ? "no events in window" :
             currentTs ? `${currentTs.toLocaleTimeString([], { hour12: false })} · ${cursorIdx}/${totalEvents}` :
             `${totalEvents} events queued`}
          </span>
          <span>now</span>
        </div>
      </div>
    </div>
  );
}

export default function ThreatMap() {
  const [mode, setMode] = useState("live");    // "live" | "playback"
  const [simulate, setSimulate] = useState(true);
  const [real, setReal] = useState(true);
  const [paused, setPaused] = useState(false);
  const [severityFilter, setSeverityFilter] = useState({
    low: true, medium: true, high: true, critical: true,
  });
  const [selected, setSelected] = useState(null);

  // Playback state
  const [pbWindow, setPbWindow] = useState(60);      // minutes
  const [pbSpeed, setPbSpeed] = useState(20);
  const [pbPlaying, setPbPlaying] = useState(true);
  const [pbProgress, setPbProgress] = useState(0);

  // Visualization mode: "map" (2D) or "globe" (3D)
  const webglSupported = useMemo(() => hasWebGLSupport(), []);
  const [viewMode, setViewMode] = useState(webglSupported ? "map" : "map");
  const [wallMode, setWallMode] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [storyEventId, setStoryEventId] = useState(null);
  const audioCtxRef = useRef(null);
  const globeCameraRef = useRef(null);   // set by ThreatGlobe when mounted

  // Enter/exit browser fullscreen with wall mode
  useEffect(() => {
    if (wallMode) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    }
    // Esc handler
    const onKey = (e) => {
      if (e.key === "Escape" && wallMode) setWallMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [wallMode]);

  // Web Audio ping — synthesized locally, no assets required.
  const playPing = (severity) => {
    if (!audioOn) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtxRef.current = audioCtxRef.current || new AC();
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = severity === "critical" ? 880 : 660;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.6);
    } catch (e) { /* ignore */ }
  };

  const liveEnabled = mode === "live" && !paused;
  const pbEnabled = mode === "playback";

  const { events, setEvents, connected } = useEventStream({
    enabled: liveEnabled,
    simulate,
    real,
  });

  const {
    buffer: pbBuffer,
    buckets: pbBuckets,
    loading: pbLoading,
    seek: pbSeek,
    restart: pbRestart,
  } = usePlaybackEngine({
    enabled: pbEnabled,
    windowMinutes: pbWindow,
    speed: pbSpeed,
    playing: pbPlaying,
    setEvents,
    onProgress: setPbProgress,
    onDone: () => setPbPlaying(false),
  });

  const filtered = useMemo(
    () => events.filter((e) => severityFilter[e.severity]),
    [events, severityFilter],
  );

  // Emit an audio ping for freshly-arrived CRIT events (works in both 2D and 3D views).
  const seenCritIdsRef = useRef(new Set());
  useEffect(() => {
    if (!audioOn) return;
    for (const ev of filtered) {
      if (ev.severity === "critical" && !seenCritIdsRef.current.has(ev.id)) {
        seenCritIdsRef.current.add(ev.id);
        playPing("critical");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, audioOn]);

  // Aggregate per-source hotspots (rolling) so long-running peaks show up bigger.
  const liveHotspots = useMemo(() => {
    const agg = new Map();
    for (const e of filtered) {
      const key = `${e.source.city}|${e.source.country}`;
      const cur = agg.get(key) || {
        city: e.source.city,
        country: e.source.country,
        lat: e.source.lat,
        lon: e.source.lon,
        count: 0,
        maxSev: 0,
      };
      cur.count += 1;
      cur.maxSev = Math.max(cur.maxSev, e.severity_weight);
      agg.set(key, cur);
    }
    return [...agg.values()].sort((a, b) => b.count - a.count);
  }, [filtered]);

  const ticker = useMemo(
    () => [...filtered].reverse().slice(0, MAX_TICKER),
    [filtered],
  );

  const arcs = useMemo(
    () => filtered.slice(-MAX_ARCS),
    [filtered],
  );

  const stats = useMemo(() => {
    const c = { low: 0, medium: 0, high: 0, critical: 0, real: 0 };
    for (const e of filtered) {
      c[e.severity] += 1;
      if (e.real) c.real += 1;
    }
    return c;
  }, [filtered]);

  const sevKeys = ["low", "medium", "high", "critical"];

  return (
    <div
      className={
        wallMode
          ? "fixed inset-0 z-[100] flex flex-col bg-[#060606] text-white"
          : "h-screen flex flex-col bg-[#060606] text-white"
      }
      data-testid="threatmap-page"
    >
      {/* Header */}
      <div className="px-8 pt-8 pb-5 border-b border-white/10">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
              <h1 className="font-display font-black text-3xl tracking-tight">
                THREAT MAP
              </h1>
              <div className="flex items-center gap-1.5 px-2 py-0.5 border border-white/10 rounded-sm" data-testid="threatmap-status">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    mode === "playback"
                      ? (pbPlaying ? "bg-[#B57BFF]" : "bg-[#FFC542]")
                      : (paused ? "bg-[#FFC542]" : connected ? "bg-[#00F0FF]" : "bg-[#8E9196]")
                  }`}
                  style={
                    mode === "playback"
                      ? (pbPlaying ? { boxShadow: "0 0 8px #B57BFF" } : undefined)
                      : (!paused && connected ? { boxShadow: "0 0 8px #00F0FF" } : undefined)
                  }
                />
                <span className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">
                  {mode === "playback"
                    ? (pbLoading ? "LOADING" : pbPlaying ? "PLAYING BACK" : "PAUSED")
                    : (paused ? "PAUSED" : connected ? "LIVE" : "CONNECTING")}
                </span>
              </div>
            </div>
            <p className="text-sm text-[#8E9196] mt-1 max-w-2xl">
              {mode === "live"
                ? "Real-time global threat feed. Simulated adversary activity is blended with real IOC pins from your active investigations for SOC-floor situational awareness."
                : `Replaying the last ${pbWindow >= 60 ? `${pbWindow / 60}h` : `${pbWindow}m`} of persisted threat events at ${pbSpeed}× speed — like a security time-lapse.`}
            </p>
          </div>

          {/* Mode switch */}
          <div className="flex items-center gap-3 shrink-0">
            {/* View mode: 2D map vs 3D globe */}
            <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm p-0.5" data-testid="threatmap-view-switch">
              <button
                onClick={() => setViewMode("map")}
                data-testid="threatmap-view-map"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm transition-colors ${
                  viewMode === "map" ? "bg-white text-black" : "text-[#8E9196] hover:text-white"
                }`}
                title="Flat 2D map"
              >
                <MapIcon className="w-3 h-3" strokeWidth={2} />
                2D
              </button>
              <button
                onClick={() => {
                  if (!webglSupported) {
                    toast.error("WebGL is unavailable on this device — 2D map is the only supported view.");
                    return;
                  }
                  setViewMode("globe");
                }}
                data-testid="threatmap-view-globe"
                disabled={!webglSupported}
                title={webglSupported ? "3D rotating globe" : "WebGL unavailable on this device"}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm transition-colors ${
                  viewMode === "globe" ? "bg-[#00F0FF] text-black" : "text-[#8E9196] hover:text-white"
                } ${!webglSupported ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <Orbit className="w-3 h-3" strokeWidth={2} />
                Globe
              </button>
            </div>

            {/* Wall mode + audio ping */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setAudioOn((v) => !v)}
                data-testid="threatmap-audio-toggle"
                title={audioOn ? "Mute alert pings" : "Play alert pings on CRIT"}
                className={`p-1.5 border rounded-sm transition-colors ${
                  audioOn ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/10 text-[#8E9196] hover:text-white"
                }`}
              >
                {audioOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </button>
              <button
                onClick={() => setWallMode((v) => !v)}
                data-testid="threatmap-wall-toggle"
                title={wallMode ? "Exit Wall Mode (Esc)" : "Enter SOC Wall Mode (fullscreen)"}
                className={`p-1.5 border rounded-sm transition-colors ${
                  wallMode ? "border-[#FFC542] text-[#FFC542]" : "border-white/10 text-[#8E9196] hover:text-white"
                }`}
              >
                {wallMode ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
            </div>

            <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm p-0.5" data-testid="threatmap-mode-switch">
              <button
                onClick={() => setMode("live")}
                data-testid="threatmap-mode-live"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm transition-colors ${
                  mode === "live" ? "bg-[#00F0FF] text-black" : "text-[#8E9196] hover:text-white"
                }`}
              >
                <Wifi className="w-3 h-3" strokeWidth={2} />
                Live
              </button>
              <button
                onClick={() => setMode("playback")}
                data-testid="threatmap-mode-playback"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm transition-colors ${
                  mode === "playback" ? "bg-[#B57BFF] text-black" : "text-[#8E9196] hover:text-white"
                }`}
              >
                <History className="w-3 h-3" strokeWidth={2} />
                Playback
              </button>
            </div>

            {mode === "live" ? (
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2" data-testid="threatmap-sim-toggle">
                  <Switch
                    checked={simulate}
                    onCheckedChange={setSimulate}
                    id="sim-toggle"
                    className="data-[state=checked]:bg-[#00F0FF]"
                  />
                  <label htmlFor="sim-toggle" className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] cursor-pointer">
                    Simulation
                  </label>
                </div>
                <div className="flex items-center gap-2" data-testid="threatmap-real-toggle">
                  <Switch
                    checked={real}
                    onCheckedChange={setReal}
                    id="real-toggle"
                    className="data-[state=checked]:bg-[#FF2E63]"
                  />
                  <label htmlFor="real-toggle" className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] cursor-pointer">
                    Real IOCs
                  </label>
                </div>
                <button
                  onClick={() => setPaused((p) => !p)}
                  data-testid="threatmap-pause-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-[#00F0FF]/50 rounded-sm text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white transition-colors"
                >
                  {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                  {paused ? "Resume" : "Pause"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Playback controls (only in playback mode) */}
        {mode === "playback" && (
          <PlaybackControls
            windowMinutes={pbWindow}
            setWindowMinutes={setPbWindow}
            speed={pbSpeed}
            setSpeed={setPbSpeed}
            playing={pbPlaying}
            setPlaying={setPbPlaying}
            progress={pbProgress}
            onSeek={pbSeek}
            onRestart={() => { pbRestart(); setPbPlaying(true); }}
            buffer={pbBuffer}
            buckets={pbBuckets}
            loading={pbLoading}
          />
        )}

        {/* Sev filter row */}
        <div className="mt-4 flex items-center gap-3">
          <Filter className="w-3.5 h-3.5 text-[#8E9196]" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mr-1">
            Severity
          </span>
          {sevKeys.map((k) => (
            <FilterPill
              key={k}
              label={SEVERITY_STYLES[k].label}
              color={SEVERITY_STYLES[k].color}
              active={severityFilter[k]}
              onClick={() =>
                setSeverityFilter((s) => ({ ...s, [k]: !s[k] }))
              }
            />
          ))}
          <div className="ml-auto flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest">
            {sevKeys.map((k) => (
              <span key={k} className="flex items-center gap-1.5 text-[#8E9196]">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: SEVERITY_STYLES[k].color }}
                />
                {SEVERITY_STYLES[k].label} · {stats[k]}
              </span>
            ))}
            <span className="flex items-center gap-1.5 text-[#8E9196]">
              <ShieldAlert className="w-3 h-3 text-[#FF2E63]" />
              REAL · {stats.real}
            </span>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="flex flex-1 min-h-0">
        {/* MAP / GLOBE */}
        <div className="relative border-r border-white/10 overflow-hidden flex-1 min-w-0">
          {viewMode === "map" ? (
            <MapCanvas
              arcs={arcs}
              events={filtered}
              hotspots={liveHotspots}
              onSelect={setSelected}
            />
          ) : (
            <GlobeErrorBoundary onFallback={({ reason }) => {
              setViewMode("map");
              if (reason === "webgl") {
                toast.error("WebGL unavailable on this device — falling back to 2D map.");
              } else if (reason === "runtime") {
                toast.warning("3D globe crashed — switched to 2D map.");
              }
            }}>
              <ThreatGlobe
                events={filtered}
                onSelect={setSelected}
                autoRotate={true}
                wallMode={wallMode}
                onCritEvent={() => { /* audio ping is handled centrally via effect */ }}
              />
            </GlobeErrorBoundary>
          )}

          {/* Wall-mode HUD: big centered counters + top-3 hotspots */}
          {wallMode && (
            <div className="absolute inset-x-0 top-4 pointer-events-none" data-testid="threatmap-wall-hud">
              <div className="mx-auto max-w-6xl px-6 flex items-start justify-between gap-6">
                <div className="flex gap-3">
                  {sevKeys.map((k) => (
                    <div key={k} className="flex flex-col items-center min-w-[92px] px-4 py-2 rounded-sm bg-black/40 backdrop-blur border border-white/10">
                      <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: SEVERITY_STYLES[k].color }}>
                        {SEVERITY_STYLES[k].label}
                      </span>
                      <span className="font-display font-black text-3xl leading-none mt-1"
                            style={{ color: SEVERITY_STYLES[k].color, textShadow: `0 0 12px ${SEVERITY_STYLES[k].color}66` }}>
                        {stats[k]}
                      </span>
                    </div>
                  ))}
                  <div className="flex flex-col items-center min-w-[92px] px-4 py-2 rounded-sm bg-black/40 backdrop-blur border border-[#FF2E63]/40">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#FF2E63]">REAL IOC</span>
                    <span className="font-display font-black text-3xl leading-none mt-1 text-[#FF2E63]"
                          style={{ textShadow: "0 0 14px #FF2E6366" }}>{stats.real}</span>
                  </div>
                </div>
                <div className="min-w-[240px] px-4 py-3 rounded-sm bg-black/40 backdrop-blur border border-white/10">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">
                    Top Origin Hotspots
                  </div>
                  {liveHotspots.slice(0, 5).map((h) => (
                    <div key={`${h.city}-${h.country}`} className="flex items-center gap-2 text-sm">
                      <span className="text-white/90 truncate flex-1">
                        {h.city} <span className="text-[#52525B]">/{h.country}</span>
                      </span>
                      <span
                        className="font-mono text-xs"
                        style={{
                          color: h.maxSev >= 4 ? "#FF2E63" : h.maxSev >= 3 ? "#FF6B35" : h.maxSev >= 2 ? "#FFC542" : "#00F0FF",
                        }}
                      >
                        {h.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Bottom-left legend + selected event card */}
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-4 pointer-events-none">
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#52525B] pointer-events-auto">
              ForensiQ · Geospatial Threat Feed · Built by <span className="text-[#00F0FF]">Ahamed Fasudeen</span>
            </div>
            {selected && (
              <div
                className="pointer-events-auto max-w-md bg-[#0a0a0a]/95 backdrop-blur border border-white/10 rounded-sm p-4"
                data-testid="threatmap-selected-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        background: SEVERITY_STYLES[selected.severity].color,
                        boxShadow: SEVERITY_STYLES[selected.severity].glow,
                      }}
                    />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white">
                      {selected.attack_label}
                    </span>
                    {selected.real && (
                      <Badge className="bg-[#FF2E63] text-black text-[8px] font-mono tracking-widest px-1.5">REAL IOC</Badge>
                    )}
                  </div>
                  <button
                    onClick={() => setSelected(null)}
                    className="text-[#8E9196] hover:text-white text-xs"
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-0.5">Source</div>
                    <div className="font-mono">{selected.source.ip}</div>
                    <div className="text-[#8E9196]">{selected.source.city}, {selected.source.country}</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196] mb-0.5">Target</div>
                    <div>{selected.target.org}</div>
                    <div className="text-[#8E9196]">{selected.target.city}, {selected.target.country}</div>
                  </div>
                </div>
                {selected.case_number && (
                  <div className="mt-2 pt-2 border-t border-white/5 text-[10px] font-mono text-[#00F0FF]">
                    Linked case: {selected.case_number}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#52525B]">
                    {new Date(selected.ts).toLocaleTimeString()}
                  </div>
                  {selected.real && (
                    <button
                      onClick={() => setStoryEventId(selected.id)}
                      data-testid="threatmap-play-story-btn"
                      className="flex items-center gap-1.5 px-2 py-1 bg-[#B57BFF]/20 border border-[#B57BFF]/60 hover:bg-[#B57BFF] hover:text-black rounded-sm text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] transition-colors"
                    >
                      ▶ Play Story
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* SIDEBAR: live ticker + top hotspots */}
        <aside className="bg-[#0a0a0a] flex flex-col w-[320px] shrink-0" data-testid="threatmap-sidebar">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-[#00F0FF]" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              Live Event Feed
            </span>
            <span className="ml-auto text-[10px] font-mono text-[#52525B]">
              {ticker.length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="threatmap-ticker">
            {ticker.map((e) => {
              const sev = SEVERITY_STYLES[e.severity];
              return (
                <button
                  key={e.id}
                  onClick={() => setSelected(e)}
                  className="w-full text-left px-4 py-2.5 border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: sev.color, boxShadow: sev.glow }}
                    />
                    <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: sev.color }}>
                      {sev.label}
                    </span>
                    <span className="text-xs text-white/90 truncate">{e.attack_label}</span>
                    {e.real && (
                      <span className="ml-auto text-[8px] font-mono tracking-widest text-[#FF2E63]">REAL</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-[#8E9196] font-mono">
                    <span className="truncate">{e.source.city}</span>
                    <span className="text-[#52525B]">→</span>
                    <span className="truncate">{e.target.city}</span>
                    <span className="ml-auto text-[#52525B]">
                      {new Date(e.ts).toLocaleTimeString([], { hour12: false })}
                    </span>
                  </div>
                </button>
              );
            })}
            {ticker.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-[#52525B] font-mono">
                Waiting for events…
              </div>
            )}
          </div>

          <div className="border-t border-white/10 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-3.5 h-3.5 text-[#FF6B35]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
                Top Origin Hotspots
              </span>
            </div>
            <div className="space-y-1.5" data-testid="threatmap-hotspots">
              {liveHotspots.slice(0, 6).map((h) => (
                <div key={`${h.city}-${h.country}`} className="flex items-center gap-2 text-xs">
                  <span className="text-white/80 truncate flex-1">
                    {h.city} <span className="text-[#52525B]">/{h.country}</span>
                  </span>
                  <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full"
                      style={{
                        width: `${Math.min(100, h.count * 8)}%`,
                        background: h.maxSev >= 4 ? "#FF2E63" : h.maxSev >= 3 ? "#FF6B35" : h.maxSev >= 2 ? "#FFC542" : "#00F0FF",
                        boxShadow: h.maxSev >= 3 ? "0 0 6px currentColor" : undefined,
                      }}
                    />
                  </div>
                  <span className="w-5 text-right font-mono text-[10px] text-[#8E9196]">{h.count}</span>
                </div>
              ))}
              {liveHotspots.length === 0 && (
                <div className="text-[10px] text-[#52525B] font-mono">No data yet.</div>
              )}
            </div>
          </div>

          <div className="border-t border-white/10 px-4 py-3 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-[#00F0FF]" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              Total in view · {filtered.length}
            </span>
          </div>
        </aside>
      </div>

      {/* CVE Radar ticker — bottom band, always visible in wall mode. */}
      {wallMode && (
        <CVETicker />
      )}

      {/* Attack Story slide-in panel */}
      <AttackStoryPanel
        eventId={storyEventId}
        onClose={() => setStoryEventId(null)}
        onCameraFocus={(pos) => {
          // Attempt globe camera focus if we're in globe view — safely.
          try { globeCameraRef.current?.(pos); } catch { /* ignore */ }
        }}
      />
    </div>
  );
}

// ------ Map canvas (separated so re-render on hover doesn't restart stream) ------
function MapCanvas({ arcs, hotspots, onSelect }) {
  return (
    <div className="w-full h-full relative" style={{ minHeight: 0 }}>
      {/* Faint scanline & grid overlay for cinematic SOC look */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,240,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,240,255,0.03) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at center, black 50%, transparent 90%)",
        }}
      />
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        width={980}
        height={520}
        style={{ width: "100%", height: "100%", background: "#060606" }}
      >
        <defs>
          <radialGradient id="ocean" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#0a1520" />
            <stop offset="100%" stopColor="#060606" />
          </radialGradient>
          <linearGradient id="arc-cyan" x1="0%" x2="100%">
            <stop offset="0%" stopColor="#00F0FF" stopOpacity="0" />
            <stop offset="50%" stopColor="#00F0FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#FF2E63" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        <rect x="-500" y="-500" width="2000" height="2000" fill="url(#ocean)" />

        <ZoomableGroup center={[10, 20]} zoom={1} maxZoom={4}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#0f1922"
                  stroke="#1a2833"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#152331", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Arcs */}
          {arcs.map((e) => {
            const sev = SEVERITY_STYLES[e.severity];
            return (
              <Line
                key={`arc-${e.id}`}
                from={[e.source.lon, e.source.lat]}
                to={[e.target.lon, e.target.lat]}
                stroke={sev.color}
                strokeWidth={e.severity_weight >= 3 ? 1.2 : 0.7}
                strokeOpacity={0.55}
                strokeLinecap="round"
                style={{
                  filter: `drop-shadow(0 0 3px ${sev.color})`,
                }}
              />
            );
          })}

          {/* Origin hotspot markers (aggregated pulses) */}
          {hotspots.slice(0, 20).map((h) => {
            const color =
              h.maxSev >= 4 ? "#FF2E63" :
              h.maxSev >= 3 ? "#FF6B35" :
              h.maxSev >= 2 ? "#FFC542" : "#00F0FF";
            const r = 3 + Math.min(8, h.count * 0.6);
            return (
              <Marker key={`h-${h.city}-${h.country}`} coordinates={[h.lon, h.lat]}>
                {/* Outer expanding pulse */}
                <circle
                  r={r + 4}
                  fill={color}
                  fillOpacity={0.15}
                  style={{
                    transformOrigin: "center",
                    animation: "fq-pulse 1.8s ease-out infinite",
                  }}
                />
                <circle r={r} fill={color} fillOpacity={0.9}
                  style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
                <circle r={r * 0.4} fill="#060606" />
              </Marker>
            );
          })}

          {/* Target markers (small cyan dot for freshest events) */}
          {arcs.slice(-6).map((e) => (
            <Marker key={`t-${e.id}`} coordinates={[e.target.lon, e.target.lat]}>
              <circle r={2.5} fill="#00F0FF" fillOpacity={0.9}
                style={{ filter: "drop-shadow(0 0 4px #00F0FF)" }} />
            </Marker>
          ))}

          {/* Clickable overlay markers on freshest events for detail popup */}
          {arcs.slice(-14).map((e) => (
            <Marker
              key={`click-${e.id}`}
              coordinates={[e.source.lon, e.source.lat]}
              onClick={() => onSelect(e)}
              style={{ default: { cursor: "pointer" } }}
            >
              <circle r={6} fill="transparent" onClick={() => onSelect(e)} data-testid={`threatmap-marker-${e.id}`} />
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      <style>{`
        @keyframes fq-pulse {
          0%   { transform: scale(0.6); opacity: 0.9; }
          80%  { transform: scale(2.6); opacity: 0; }
          100% { transform: scale(2.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
